import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Capacitor } from '@capacitor/core';
import { isNativeApp } from '../config';
import { GoogleAuth } from '@codetrix-studio/capacitor-google-auth';

/**
 * GoogleAuthButton Component
 * Supports Google Identity Services (GSI) OAuth 2.0 and fallback dev/demo testing.
 */
const GoogleAuthButton = ({ mode = 'login', onSuccess, onError }) => {
  const { t } = useTranslation();
  const { loginWithGoogle: sendGoogleSignIn } = useAuth();
  const [loading, setLoading] = useState(false);
  // A first-time Google sign-up waiting for the terms to be accepted
  const [pendingTerms, setPendingTerms] = useState(null);
  const [acceptingTerms, setAcceptingTerms] = useState(false);

  // Signs in. When Google's account is new to Bazzef, the server creates nothing yet and
  // asks for the terms first; the dialog below then repeats the request with them accepted.
  const loginWithGoogle = async (payload) => {
    const res = await sendGoogleSignIn(payload);
    if (res.needsTerms) {
      setShowConfigModal(false);
      setPendingTerms({ payload, profile: res.profile });
      return { success: false, pendingTerms: true };
    }
    return res;
  };

  const handleAcceptTerms = async () => {
    setAcceptingTerms(true);
    const res = await sendGoogleSignIn({ ...pendingTerms.payload, acceptTerms: true });
    setAcceptingTerms(false);
    setPendingTerms(null);
    if (res.success) {
      if (onSuccess) onSuccess(res.user);
    } else if (onError) {
      onError(res.error || 'Google sign-in failed');
    }
  };
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const googleBtnContainerRef = useRef(null);

  const [googleClientId, setGoogleClientId] = useState(
    process.env.REACT_APP_GOOGLE_CLIENT_ID || null
  );

  // Fetch client ID from server if not baked into React build
  useEffect(() => {
    if (!googleClientId) {
      axios
        .get('/api/auth/config')
        .then((res) => {
          if (res.data?.googleClientId) {
            setGoogleClientId(res.data.googleClientId);
          }
        })
        .catch(() => {});
    }
  }, [googleClientId]);

  // Pre-initialize GoogleAuth for native platform (Android / iOS)
  useEffect(() => {
    if (isNativeApp() && googleClientId) {
      const nativeOptions = {
        scopes: ['profile', 'email'],
        grantOfflineAccess: false,
      };
      if (Capacitor.getPlatform() !== 'ios') {
        nativeOptions.clientId = googleClientId;
        nativeOptions.serverClientId = googleClientId;
      }
      GoogleAuth.initialize(nativeOptions).catch((err) => {
        console.warn('Failed to pre-initialize GoogleAuth:', err);
      });
    }
  }, [googleClientId]);

  // Load Google Identity Services script dynamically
  useEffect(() => {
    if (!googleClientId) return;

    if (window.google?.accounts?.id) {
      initGoogleSignIn();
      return;
    }

    const scriptId = 'google-gsi-client';
    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        initGoogleSignIn();
      };
      script.onerror = () => {
        console.warn('Google Identity Services script failed to load');
      };
      document.body.appendChild(script);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleClientId]);

  const initGoogleSignIn = () => {
    if (!window.google?.accounts?.id || !googleClientId) return;

    try {
      window.google.accounts.id.initialize({
        client_id: googleClientId,
        callback: handleCredentialResponse,
        auto_select: false,
        cancel_on_tap_outside: true
      });
    } catch (err) {
      console.warn('Failed to initialize Google Accounts ID:', err);
    }
  };

  const handleCredentialResponse = async (response) => {
    if (!response.credential) {
      if (onError) onError('No credential returned from Google');
      return;
    }

    setLoading(true);
    try {
      const res = await loginWithGoogle({ credential: response.credential });
      if (res.success) {
        if (onSuccess) onSuccess(res.user);
      } else {
        if (onError && !res.pendingTerms) onError(res.error || 'Google sign-in failed');
      }
    } catch (err) {
      if (onError) onError(err.message || 'Google sign-in failed');
    } finally {
      setLoading(false);
    }
  };

  const triggerOAuthFlow = (clientId) => {
    if (window.google?.accounts?.oauth2) {
      setLoading(true);
      try {
        const tokenClient = window.google.accounts.oauth2.initTokenClient({
          client_id: clientId,
          scope: 'email profile openid',
          callback: async (tokenResponse) => {
            if (tokenResponse.error) {
              setLoading(false);
              if (onError) onError(tokenResponse.error_description || tokenResponse.error);
              return;
            }
            if (tokenResponse.access_token) {
              const res = await loginWithGoogle({ accessToken: tokenResponse.access_token });
              if (res.success) {
                if (onSuccess) onSuccess(res.user);
              } else {
                if (onError && !res.pendingTerms) onError(res.error || 'Google sign-in failed');
              }
            }
            setLoading(false);
          },
          error_callback: (err) => {
            setLoading(false);
            console.warn('Google OAuth popup error:', err);
            if (onError) onError(err.message || 'Google sign-in popup cancelled');
          },
        });
        tokenClient.requestAccessToken({ prompt: 'consent' });
      } catch (err) {
        setLoading(false);
        console.warn('Token client invocation failed, falling back to prompt:', err);
        if (window.google?.accounts?.id) {
          window.google.accounts.id.prompt();
        }
      }
    } else if (window.google?.accounts?.id) {
      window.google.accounts.id.prompt();
    } else {
      setShowConfigModal(true);
    }
  };

  const handleClick = async () => {
    let activeClientId = googleClientId;
    if (!activeClientId) {
      setLoading(true);
      try {
        const res = await axios.get('/api/auth/config');
        if (res.data?.googleClientId) {
          activeClientId = res.data.googleClientId;
          setGoogleClientId(activeClientId);
        }
      } catch (_) {}
      setLoading(false);
    }

    if (!activeClientId) {
      setShowConfigModal(true);
      return;
    }

    // 1. Native mobile flow via Google Play Services / ASWebAuthenticationSession
    if (isNativeApp()) {
      setLoading(true);
      try {
        // iOS Google Sign-In redirects through a custom URL scheme. That is
        // rejected when the client id is the web client ("Custom scheme URLs
        // are not allowed for 'WEB' client type"). Leave clientId unset so the
        // plugin uses iosClientId from the Capacitor config.
        const nativeOptions = {
          scopes: ['profile', 'email'],
          grantOfflineAccess: false,
        };
        if (Capacitor.getPlatform() !== 'ios') {
          nativeOptions.clientId = activeClientId;
          nativeOptions.serverClientId = activeClientId;
        }
        await GoogleAuth.initialize(nativeOptions);
        const googleUser = await GoogleAuth.signIn();
        const credential = googleUser.authentication?.idToken;
        const accessToken = googleUser.authentication?.accessToken;
        if (credential || accessToken) {
          const res = await loginWithGoogle({ credential, accessToken });
          if (res.success) {
            if (onSuccess) onSuccess(res.user);
          } else {
            if (onError && !res.pendingTerms) onError(res.error || 'Google sign-in failed');
          }
        } else {
          if (onError) onError('No credential returned from native Google Sign-In');
        }
      } catch (err) {
        console.warn('Native Google sign-in error:', err, 'code:', err?.code);
        const errMsg = err?.message || String(err);
        if (!errMsg.toLowerCase().includes('cancel') && !errMsg.toLowerCase().includes('12501')) {
          if (onError) onError(errMsg || 'Google sign-in failed');
        }
      } finally {
        setLoading(false);
      }
      return;
    }

    // 2. Web browser flow via Google Identity Services (GSI)
    if (!window.google?.accounts) {
      setLoading(true);
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.onload = () => {
        setLoading(false);
        triggerOAuthFlow(activeClientId);
      };
      script.onerror = () => {
        setLoading(false);
        if (onError) onError('Failed to load Google Sign-In SDK. If using an ad-blocker or Brave Shields, please allow accounts.google.com');
      };
      document.body.appendChild(script);
      return;
    }

    triggerOAuthFlow(activeClientId);
  };

  // Handle Demo test sign-in
  const handleDemoSignIn = async () => {
    setDemoLoading(true);
    try {
      const res = await loginWithGoogle({
        demoUser: {
          id: 'demo_' + Date.now().toString().slice(-4),
          email: 'google.demo@bazzef.com',
          name: 'Google Demo User',
          picture: 'https://lh3.googleusercontent.com/a/default-user=s96-c'
        }
      });
      if (res.success) {
        setShowConfigModal(false);
        if (onSuccess) onSuccess(res.user);
      } else {
        if (onError && !res.pendingTerms) onError(res.error || 'Demo sign-in failed');
      }
    } catch (err) {
      if (onError) onError(err.message || 'Demo sign-in failed');
    } finally {
      setDemoLoading(false);
    }
  };

  const buttonText = mode === 'register'
    ? t('auth.signUpWithGoogle', 'Sign up with Google')
    : t('auth.continueWithGoogle', 'Continue with Google');

  return (
    <>
      <div className="w-full">
        <button
          type="button"
          onClick={handleClick}
          disabled={loading}
          className="w-full flex items-center justify-center gap-2.5 py-2.5 px-4 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 hover:bg-zinc-50 dark:hover:bg-zinc-800/80 text-zinc-800 dark:text-zinc-200 font-medium text-sm transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-sky-500/20 active:scale-[0.99] disabled:opacity-60 shadow-sm"
        >
          {loading ? (
            <div className="w-4 h-4 border-2 border-zinc-400 border-t-transparent rounded-full animate-spin" />
          ) : (
            <svg className="w-[18px] h-[18px] flex-shrink-0" width="18" height="18" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.8-2.4 3.65v3.03h3.88c2.28-2.1 3.66-5.2 3.66-9.12z"
              />
              <path
                fill="#34A853"
                d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.88-3.03c-1.08.72-2.45 1.16-4.05 1.16-3.12 0-5.77-2.1-6.72-4.93H1.24v3.13C3.26 21.36 7.33 24 12 24z"
              />
              <path
                fill="#FBBC05"
                d="M5.28 14.29c-.25-.72-.38-1.49-.38-2.29s.13-1.57.38-2.29V6.58H1.24C.45 8.16 0 9.97 0 12s.45 3.84 1.24 5.42l4.04-3.13z"
              />
              <path
                fill="#EA4335"
                d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.33 0 3.26 2.64 1.24 6.58l4.04 3.13c.95-2.83 3.6-4.96 6.72-4.96z"
              />
            </svg>
          )}
          <span>{loading ? t('common.loading', 'Loading...') : buttonText}</span>
        </button>

        {/* Hidden container for Google's native iframe button if initialized */}
        <div ref={googleBtnContainerRef} className="hidden" />
      </div>

      {/* First-time Google sign-up: the account is created only after the terms are accepted */}
      {pendingTerms && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="google-terms-title"
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-in fade-in duration-200"
        >
          <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl p-6 max-w-sm w-full shadow-2xl space-y-4">
            <h3 id="google-terms-title" className="text-base font-bold text-zinc-900 dark:text-zinc-100">
              {t('account.termsDialogTitle', 'One more step')}
            </h3>
            <p className="text-sm text-zinc-600 dark:text-zinc-300">
              {t('account.termsDialogBody', 'You are creating a Bazzef account for {{email}}. To continue, agree to our', { email: pendingTerms.profile?.email || '' })}{' '}
              <Link to="/terms" target="_blank" rel="noopener" className="font-semibold">
                {t('site.termsTitle', 'Terms of Use')}
              </Link>{' '}
              {t('account.and', 'and')}{' '}
              <Link to="/privacy" target="_blank" rel="noopener" className="font-semibold">
                {t('site.privacyTitle', 'Privacy Policy')}
              </Link>
              .
            </p>
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={handleAcceptTerms}
                disabled={acceptingTerms}
                className="w-full py-2.5 px-4 rounded-xl bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 font-semibold text-sm disabled:opacity-60"
              >
                {acceptingTerms ? t('common.loading', 'Loading...') : t('account.termsAgree', 'Agree and create account')}
              </button>
              <button
                type="button"
                onClick={() => setPendingTerms(null)}
                disabled={acceptingTerms}
                className="w-full py-2 px-4 text-xs font-semibold text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200"
              >
                {t('common.cancel', 'Cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Setup / Demo Modal when Client ID is not yet configured */}
      {showConfigModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-in fade-in duration-200">
          <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl p-6 max-w-md w-full shadow-2xl space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-sky-50 dark:bg-sky-950/40 flex items-center justify-center text-sky-600 dark:text-sky-400">
                <i className="fab fa-google text-xl" />
              </div>
              <div>
                <h3 className="text-base font-bold text-zinc-900 dark:text-zinc-100">
                  Google Sign-In Configuration
                </h3>
                <p className="text-xs text-zinc-500 dark:text-zinc-400">
                  Social login & registration
                </p>
              </div>
            </div>

            <div className="text-sm text-zinc-600 dark:text-zinc-300 space-y-2 bg-zinc-50 dark:bg-zinc-800/50 p-3.5 rounded-xl border border-zinc-100 dark:border-zinc-800">
              <p>
                To enable live Google accounts, configure your Google Cloud Client ID in your project:
              </p>
              <div className="font-mono text-xs bg-zinc-200/70 dark:bg-zinc-950 p-2 rounded-lg text-zinc-800 dark:text-zinc-200 overflow-x-auto select-all">
                REACT_APP_GOOGLE_CLIENT_ID=your-client-id.apps.googleusercontent.com
              </div>
              <p className="text-xs text-zinc-500 dark:text-zinc-400">
                Add this to your client environment or root <code className="text-xs bg-zinc-200/50 dark:bg-zinc-800 px-1 py-0.5 rounded">.env</code> file.
              </p>
            </div>

            <div className="pt-1 flex flex-col gap-2">
              <button
                type="button"
                onClick={handleDemoSignIn}
                disabled={demoLoading}
                className="w-full flex items-center justify-center gap-2 py-2.5 px-4 bg-sky-500 hover:bg-sky-600 text-white rounded-xl font-medium text-sm transition-colors shadow-xs"
              >
                {demoLoading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <i className="fas fa-play text-xs" />
                )}
                <span>Test with Demo Google Account</span>
              </button>

              <button
                type="button"
                onClick={() => setShowConfigModal(false)}
                className="w-full py-2 px-4 text-xs font-semibold text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 text-center transition-colors"
              >
                {t('common.close', 'Close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GoogleAuthButton;
